import { TrophyIcon } from '@bravemove/design-tokens/icons';
import { Avatar } from '../Avatar/Avatar';
import { Table, type TableColumn } from '../Table/Table';
import { cn } from '../../utils/cn';

/**
 * F41 — Round-robin / league standings table (web).
 *
 * Ranks `StandingsRow[]` (rank · participant · P/W/L/D · score diff · points),
 * with a podium treatment on the top three (rank 1 gets a gold trophy chip +
 * an accent name) so the leader reads at a glance. Composes the shared `Table`
 * primitive so it inherits sticky-header + density + sortable columns. Rows
 * render in the order given; the `rank` field drives the displayed position
 * (`null` → "–" pre-computation).
 *
 * Engineering-authored, token-driven.
 *
 * `StandingsRow` is structurally identical to the `@bravemove/types` shape —
 * mirrored locally (and not imported) to keep `@bravemove/ui-web` dep-free.
 */

/** Structurally identical to `StandingsRow` from `@bravemove/types`. */
export interface StandingsRow {
  readonly registrationId: string;
  readonly displayName: string;
  readonly played: number;
  readonly won: number;
  readonly lost: number;
  readonly drawn: number;
  readonly points: number;
  readonly scoreFor: number;
  readonly scoreAgainst: number;
  readonly scoreDiff: number;
  readonly rank: number | null;
}

export interface StandingsTableProps {
  readonly rows: readonly StandingsRow[];
  /** Fired when a row is clicked — typically opens the participant's matches. */
  readonly onRowPress?: ((row: StandingsRow) => void) | undefined;
  readonly className?: string | undefined;
  readonly density?: 'cosy' | 'compact' | undefined;
  readonly ariaLabel?: string | undefined;
}

/** Podium chip tone for the top three; everyone else gets a plain number. */
const PODIUM_CHIP: Record<1 | 2 | 3, string> = {
  1: 'bg-primary text-primary-ink',
  2: 'bg-surface-2 text-ink-2 border border-hairline-2',
  3: 'bg-secondary/15 text-secondary',
};

function RankCell({ rank }: { rank: number | null }) {
  if (rank === null) {
    return <span className="font-mono text-[13px] tabular-nums text-ink-3">–</span>;
  }
  if (rank === 1) {
    return (
      <span className="inline-flex h-6 min-w-6 items-center justify-center gap-0.5 rounded-pill bg-accent px-1 text-accent-ink">
        <TrophyIcon size={12} color="currentColor" />
        <span className="font-mono text-[12px] font-bold tabular-nums">1</span>
      </span>
    );
  }
  if (rank === 2 || rank === 3) {
    return (
      <span
        className={cn(
          'inline-flex h-6 min-w-6 items-center justify-center rounded-pill px-1.5 font-mono text-[12px] font-bold tabular-nums',
          PODIUM_CHIP[rank],
        )}
      >
        {rank}
      </span>
    );
  }
  return <span className="font-mono text-[13px] font-bold tabular-nums text-ink-2">{rank}</span>;
}

export function StandingsTable({
  rows,
  onRowPress,
  className,
  density = 'cosy',
  ariaLabel = 'Standings',
}: StandingsTableProps) {
  const columns: readonly TableColumn<StandingsRow>[] = [
    {
      key: 'rank',
      header: '#',
      width: '3.25rem',
      align: 'center',
      cell: (row) => <RankCell rank={row.rank} />,
    },
    {
      key: 'participant',
      header: 'Participant',
      cell: (row) => (
        <span className="inline-flex min-w-0 items-center gap-2">
          <Avatar name={row.displayName} size="sm" />
          <span
            className={cn(
              'truncate text-[13px]',
              row.rank === 1 ? 'font-bold text-accent' : 'font-medium text-ink',
            )}
          >
            {row.displayName}
          </span>
        </span>
      ),
    },
    {
      key: 'played',
      header: 'P',
      width: '2.75rem',
      align: 'center',
      sortable: true,
      sortAccessor: (row) => row.played,
      cell: (row) => <span className="font-mono tabular-nums text-ink-2">{row.played}</span>,
    },
    {
      key: 'won',
      header: 'W',
      width: '2.75rem',
      align: 'center',
      sortable: true,
      sortAccessor: (row) => row.won,
      cell: (row) => (
        <span className="font-mono font-semibold tabular-nums text-success">{row.won}</span>
      ),
    },
    {
      key: 'lost',
      header: 'L',
      width: '2.75rem',
      align: 'center',
      sortable: true,
      sortAccessor: (row) => row.lost,
      cell: (row) => <span className="font-mono tabular-nums text-ink-3">{row.lost}</span>,
    },
    {
      key: 'drawn',
      header: 'D',
      width: '2.75rem',
      align: 'center',
      sortable: true,
      sortAccessor: (row) => row.drawn,
      cell: (row) => <span className="font-mono tabular-nums text-ink-3">{row.drawn}</span>,
    },
    {
      key: 'diff',
      header: '+/−',
      width: '3.5rem',
      align: 'center',
      sortable: true,
      sortAccessor: (row) => row.scoreDiff,
      cell: (row) => (
        <span
          className={cn(
            'font-mono tabular-nums',
            row.scoreDiff > 0 ? 'text-success' : row.scoreDiff < 0 ? 'text-danger' : 'text-ink-3',
          )}
        >
          {row.scoreDiff > 0 ? `+${row.scoreDiff}` : row.scoreDiff}
        </span>
      ),
    },
    {
      key: 'points',
      header: 'Pts',
      width: '3.5rem',
      align: 'center',
      sortable: true,
      sortAccessor: (row) => row.points,
      cell: (row) => (
        <span
          className={cn(
            'font-mono text-[13.5px] font-bold tabular-nums',
            row.rank === 1 ? 'text-accent' : 'text-ink',
          )}
        >
          {row.points}
        </span>
      ),
    },
  ];

  return (
    <Table
      data={rows}
      columns={columns}
      rowKey={(row) => row.registrationId}
      {...(onRowPress ? { onRowPress } : {})}
      {...(className ? { className } : {})}
      density={density}
      ariaLabel={ariaLabel}
      empty="No standings yet"
    />
  );
}

export default StandingsTable;
